import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import Icon from 'components/AppIcon';
import AppTooltip from 'components/Tooltip';

const COLORS = ['#EA580C', '#6366f1', '#059669', '#D97706', '#06b6d4', '#8b5cf6', '#ec4899', '#64748b', '#0ea5e9', '#f59e0b'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 text-xs" style={{ boxShadow: 'var(--shadow-lg)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <p className="font-semibold text-foreground mb-1">{label || payload?.[0]?.name}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color }}>{p?.name}: {typeof p?.value === 'number' ? p?.value?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : p?.value}{p?.name === 'Exposición' ? ' €' : p?.name === 'Pct' ? '%' : ''}</p>
      ))}
    </div>
  );
};

const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>
      {(percent * 100)?.toFixed(0)}%
          </text>
  );
};

export default function RiskConcentrationPanel({ batches, riskThresholds }) {
  const maxConcentration = riskThresholds?.max_concentration_pct || 40;

  const { byMetal, byCategory } = useMemo(() => {
    const activeBatches = batches?.filter(b =>
      ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)
    ) || [];

    const metalMap = {};
    const catMap = {};

    activeBatches?.forEach(b => {
      const exposure = (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0);
      const metal = b?.metal_name || 'Sin Metal';
      const cat = b?.category_name || 'Sin Categoría';
      metalMap[metal] = (metalMap?.[metal] || 0) + exposure;
      catMap[cat] = (catMap?.[cat] || 0) + exposure;
    });

    const totalExposure = Object.values(metalMap)?.reduce((s, v) => s + v, 0);

    const byMetal = Object.entries(metalMap)?.map(([name, value]) => ({ name, value: Math.round(value), pct: totalExposure > 0 ? (value / totalExposure * 100) : 0 }))?.sort((a, b) => b?.value - a?.value);

    const byCategory = Object.entries(catMap)?.map(([name, value]) => ({ name, value: Math.round(value) }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 10);

    return { byMetal, byCategory };
  }, [batches]);

  const warnings = byMetal?.filter(m => m?.pct > maxConcentration);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="PieChart" size={18} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            <AppTooltip
              title="Concentración de Riesgo"
              description="Distribución de la exposición total entre metales y categorías. Una alta concentración en un solo metal aumenta el riesgo ante caídas de precio."
              example="Si el 70% de la exposición es en Cobre, una caída del LME afecta gravemente la cartera"
            >
              Concentración de Riesgo
            </AppTooltip>
          </h2>
        </div>
        {warnings?.length > 0 && (
          <AppTooltip
            title="Metales sobre umbral"
            description={`${warnings?.length} metal${warnings?.length > 1 ? 'es superan' : ' supera'} el umbral máximo de concentración del ${maxConcentration}% configurado en Ajustes.`}
          >
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200">
              <Icon name="AlertTriangle" size={12} color="#D97706" />
              <span className="text-xs font-semibold text-amber-700">{warnings?.length} metal{warnings?.length > 1 ? 'es' : ''} sobre umbral</span>
            </div>
          </AppTooltip>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">
        {/* Pie chart */}
        <div className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            <AppTooltip
              title="Exposición por Metal"
              description="Porcentaje de la exposición total (€) que representa cada metal. Los porcentajes se calculan sobre el total de lotes activos."
            >
              Exposición por Metal
            </AppTooltip>
          </p>
          {byMetal?.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">Sin datos</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={byMetal} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} labelLine={false} label={<PieLabel />}>
                    {byMetal?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {byMetal?.map((m, i) => (
                  <div key={m?.name} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS?.[i % COLORS?.length] }} />
                    <span className="text-xs text-muted-foreground">{m?.name}</span>
                    {m?.pct > maxConcentration && (
                      <AppTooltip
                        title={`Concentración alta: ${m?.pct?.toFixed(0)}%`}
                        description={`Este metal supera el umbral máximo de concentración del ${maxConcentration}%. Considera diversificar para reducir el riesgo.`}
                      >
                        <span className="text-xs text-amber-600 font-semibold">⚠ {m?.pct?.toFixed(0)}%</span>
                      </AppTooltip>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Bar chart */}
        <div className="p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            <AppTooltip
              title="Top Categorías por Exposición"
              description="Las 10 categorías de chatarra con mayor exposición económica (€). Calculado como Precio de Compra × Peso para todos los lotes activos de cada categoría."
            >
              Top Categorías por Exposición (€)
            </AppTooltip>
          </p>
          {byCategory?.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byCategory} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Exposición" radius={[0, 4, 4, 0]}>
                  {byCategory?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
